import { useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { usePlaygroundState } from '@/modules/playground/store/playground.store';
import { ENUM_CARD_EVENT_METRIC, TrackCardMetricDto, TrackPostMetricDto } from '@linkie/domain';
import { ENUM_EMAIL_COLLECTION_EVENT } from '@linkie/shared';
import { getDateRange } from '@linkie/shared';
import { useTrackVisitor } from '@linkie/shared';
import { useTrackEmailCollection } from '@linkie/shared';
import { trackCardMetricsBatch } from '@linkie/api-client';
import { trackPostMetricsBatch } from '@linkie/api-client';
import getUserAgentInfo from '../utils/get-user-agent-info';
import getReferrer from '../utils/get-referrer';
import { AnalyticsContext, DateRange } from './analytics.context';
import useConsent from '@/core/hooks/useConsent.hook';
import { CONSENT_CATEGORY } from '@/consent/contract';

type AnalyticsProviderProps = {
  children: ReactNode;
};

type EventDetails = {
  anonymous?: true;
  device?: string;
  browser?: string;
  os?: string;
  source?: string;
};

function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const pageId = usePlaygroundState((s) => s.payload._id);
  const isPaid = usePlaygroundState((s) => s.payload.is_paid);
  const trackVisitor = useTrackVisitor();
  const trackEmailCollection = useTrackEmailCollection();

  const analyticsAllowed = useConsent(CONSENT_CATEGORY.ANALYTICS);

  const isTrackingDisabled = () => !pageId || isPaid === false || window.location.pathname === '/playground';

  const detailsFor = async (): Promise<EventDetails> => {
    if (!analyticsAllowed) return { anonymous: true };

    const { device, browser, os } = getUserAgentInfo();

    return { device, browser, os, source: await getReferrer() };
  };

  const cardMetricQueue = useRef<TrackCardMetricDto[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const postMetricQueue = useRef<TrackPostMetricDto[]>([]);
  const postFlushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushCardMetrics = useCallback(() => {
    if (cardMetricQueue.current.length === 0) return;
    const batch = [...cardMetricQueue.current];
    cardMetricQueue.current = [];
    trackCardMetricsBatch(batch).catch(() => {});
  }, []);

  const flushPostMetrics = useCallback(() => {
    if (postMetricQueue.current.length === 0) return;
    const batch = [...postMetricQueue.current];
    postMetricQueue.current = [];
    trackPostMetricsBatch(batch).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      if (postFlushTimer.current) clearTimeout(postFlushTimer.current);
      flushCardMetrics();
      flushPostMetrics();
    };
  }, [flushCardMetrics, flushPostMetrics]);

  useEffect(() => {
    if (analyticsAllowed) return;

    const anonymise = <T extends TrackCardMetricDto | TrackPostMetricDto>(events: T[]): T[] =>
      events.map((event) => ({
        ...event,
        device: undefined,
        browser: undefined,
        os: undefined,
        source: undefined,
        anonymous: true,
      }));

    cardMetricQueue.current = anonymise(cardMetricQueue.current);
    postMetricQueue.current = anonymise(postMetricQueue.current);
  }, [analyticsAllowed]);

  const initialDateRange = getDateRange();
  const [dateFilter, setDateFilter] = useState<DateRange>({
    start: initialDateRange.start.toISOString(),
    end: initialDateRange.end.toISOString(),
  });

  const [filters, setFilters] = useState<{
    source: string;
    country: string;
    device: string;
    browser: string;
    os: string;
  }>({
    source: '',
    country: '',
    device: '',
    browser: '',
    os: '',
  });

  const updateFilter = (key: keyof NonNullable<typeof filters>, value: string) => {
    setFilters((prev) => {
      const base = prev ?? {
        source: '',
        country: '',
        device: '',
        browser: '',
        os: '',
      };
      return {
        ...base,
        [key]: value,
      };
    });
  };

  const captureVisitorMetric = async () => {
    if (isTrackingDisabled()) return;

    const details = await detailsFor();

    trackVisitor.mutate({
      page_id: pageId!,
      anonymous: details.anonymous,
      device: details.device,
      browser: details.browser,
      os: details.os,
      source: details.source,
    });
  };

  const enrichVisitorMetric = async () => {
    if (isTrackingDisabled() || !analyticsAllowed) return;

    const details = await detailsFor();

    trackVisitor.mutate({
      page_id: pageId!,
      enrich_only: true,
      device: details.device,
      browser: details.browser,
      os: details.os,
      source: details.source,
    });
  };

  const captureBannerClick = async () => {
    if (isTrackingDisabled()) return;

    const details = await detailsFor();

    trackVisitor.mutate({
      page_id: pageId!,
      anonymous: details.anonymous,
      banner_analytics: {
        total_clicks: 1,
        device: details.device,
        browser: details.browser,
        os: details.os,
        source: details.source,
      },
    });
  };

  const captureCtaClick = async () => {
    if (isTrackingDisabled()) return;

    const details = await detailsFor();

    trackVisitor.mutate({
      page_id: pageId!,
      anonymous: details.anonymous,
      cta_button_analytics: {
        total_clicks: 1,
        device: details.device,
        browser: details.browser,
        os: details.os,
        source: details.source,
      },
    });
  };

  const captureCardMetric = async (cardId: string, event: ENUM_CARD_EVENT_METRIC) => {
    if (isTrackingDisabled()) return;

    const details = await detailsFor();

    cardMetricQueue.current.push({
      page_id: pageId!,
      card_id: cardId,
      event,
      anonymous: details.anonymous,
      device: details.device,
      browser: details.browser,
      os: details.os,
      source: details.source,
    });

    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushCardMetrics, 1500);
  };

  const capturePostMetric = async (postId: string, event: ENUM_CARD_EVENT_METRIC) => {
    if (isTrackingDisabled()) return;

    const details = await detailsFor();

    postMetricQueue.current.push({
      page_id: pageId!,
      post_id: postId,
      event,
      anonymous: details.anonymous,
      device: details.device,
      browser: details.browser,
      os: details.os,
      source: details.source,
    });

    if (postFlushTimer.current) clearTimeout(postFlushTimer.current);
    postFlushTimer.current = setTimeout(flushPostMetrics, 1500);
  };

  const captureEmailCollectionMetric = async (event: ENUM_EMAIL_COLLECTION_EVENT) => {
    if (isTrackingDisabled()) return;

    const details = await detailsFor();

    trackEmailCollection.mutate({
      page_id: pageId!,
      event,
      anonymous: details.anonymous,
      device: details.device,
      browser: details.browser,
      os: details.os,
      source: details.source,
    });
  };

  const value = {
    captureVisitorMetric,
    enrichVisitorMetric,
    captureBannerClick,
    captureCtaClick,
    captureCardMetric,
    capturePostMetric,
    captureEmailCollectionMetric,
    dateFilter,
    setDateFilter,
    filters,
    updateFilter,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export default AnalyticsProvider;
